import { Alert, Box, Button, Card, Input, Text, Title } from '@nimbus-ds/components';
import { describirRegla } from '@precios/rules/describir';
import { useEffect, useMemo, useState, type ReactElement, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  api,
  type Ajuste,
  type Alcance,
  type CampoAfectado,
  type Redondeo,
  type Regla,
  type ResumenPreview,
} from '../api';
import { useConteoDeAlcance, type EstadoDelConteo } from '../alcance';
import { useNombresDeCategorias } from '../categorias';
import { Cargando, ErrorCaja } from '../componentes/Estado';
import {
  ALCANCE_INICIAL,
  CamposDeAjuste,
  CamposDeAlcance,
  sinCriterio,
} from '../componentes/CamposDeRegla';
import { aErrorVisible, esFreno, type ErrorVisible } from '../errores';
import { Layout } from '../componentes/Layout';
import { TablaItems } from '../componentes/TablaItems';
import { fechaHoraRelativa, numero } from '../formato';
import { useSesion } from '../sesion';

type Paso = 1 | 2 | 3;

/**
 * Tope del campo `nombre` en `previewBodySchema`. Los dos números tienen que coincidir.
 *
 * *** No es teórico. *** Una regla con dos categorías de ruta larga, una marca, un rango de
 * precios, redondeo por múltiplo y "precio y oferta" produce 223 caracteres — medido, no
 * estimado. Sin recortar, el preview volvía con un `400 cuerpo_invalido` y el comerciante
 * no tenía forma de saber que la culpa era de un nombre que ni siquiera escribió él.
 */
const MAX_NOMBRE = 120;

function recortar(texto: string): string {
  if (texto.length <= MAX_NOMBRE) return texto;
  return `${texto.slice(0, MAX_NOMBRE - 1).trimEnd()}…`;
}

export function NuevaActualizacion(): ReactElement {
  const navigate = useNavigate();
  const [paso, setPaso] = useState<Paso>(1);
  const sesion = useSesion();
  // El cartel con el motivo lo pone el Layout; acá sólo se cierra la puerta. El backend
  // devuelve 402 igual: esto evita que el comerciante llegue hasta el final para nada.
  const bloqueada = sesion !== null && !sesion.acceso.puedeActualizar;

  /** Vacío significa "usá el sugerido". No arranca con nada escrito a propósito. */
  const [nombre, setNombre] = useState('');
  const [alcance, setAlcance] = useState<Alcance>(ALCANCE_INICIAL);
  const [ajuste, setAjuste] = useState<Ajuste>({ kind: 'percentage', percent: 12 });
  const [redondeo, setRedondeo] = useState<Redondeo>({ kind: 'none' });
  const [campos, setCampos] = useState<CampoAfectado>('price');

  const [preview, setPreview] = useState<ResumenPreview | null>(null);
  const [calculando, setCalculando] = useState(false);
  const [ejecutando, setEjecutando] = useState(false);
  const [error, setError] = useState<ErrorVisible | null>(null);

  /**
   * *** Repetir una actualización. ***
   *
   * Es lo que va a pedir el primer comerciante que use la app dos meses seguidos: hizo +12%
   * en Relojería el mes pasado y quiere lo mismo ahora. Antes tenía que rearmar la regla
   * desde cero, con el riesgo de que le saliera parecida pero no igual.
   *
   * *** Precarga y nada más: nunca ejecuta directo. *** El wizard arranca en el paso 1 con
   * todo cargado y editable, y la vista previa se calcula igual que siempre. Los precios de
   * hoy no son los de aquella vez, así que el plan tiene que volver a mirarse.
   *
   * Va por la URL y no por el estado del router: así sobrevive a un F5, que es lo que hace
   * cualquiera que se pierde en el medio de un formulario.
   */
  const [parametros] = useSearchParams();
  const repetirId = parametros.get('repetir');
  const [copiadaDe, setCopiadaDe] = useState<string | null>(null);
  const [copiando, setCopiando] = useState(repetirId !== null);
  const [noSePudoCopiar, setNoSePudoCopiar] = useState(false);

  useEffect(() => {
    if (repetirId === null) return;
    let vigente = true;

    api
      .corrida(repetirId)
      .then(({ corrida }) => {
        if (!vigente) return;
        const spec = corrida.regla?.spec ?? null;
        if (spec === null) {
          // La corrida existe pero su regla no se puede leer con el esquema de hoy. El
          // wizard sigue andando con los valores por defecto: es un formulario vacío, no
          // una pantalla rota.
          setNoSePudoCopiar(true);
        } else {
          setAlcance(spec.scope);
          setAjuste(spec.adjustment);
          setRedondeo(spec.rounding);
          setCampos(spec.targetFields);
          setCopiadaDe(fechaHoraRelativa(corrida.creadaEn));
        }
      })
      .catch(() => {
        if (vigente) setNoSePudoCopiar(true);
      })
      .finally(() => {
        if (vigente) setCopiando(false);
      });

    return () => {
      vigente = false;
    };
  }, [repetirId]);

  const regla: Regla = useMemo(
    () => ({ scope: alcance, adjustment: ajuste, rounding: redondeo, targetFields: campos }),
    [alcance, ajuste, redondeo, campos],
  );

  /*
    Cuánto agarra el alcance, desde el paso 1. Rebota, cachea y no bloquea nada: el detalle
    está en `alcance.ts`.
  */
  const conteo = useConteoDeAlcance(alcance);

  /*
    El nombre por defecto: la regla en una línea.

    Los nombres de categoría ya los pidió `CamposDeAlcance` cuando el comerciante eligió
    "sólo algunos", y `api.categorias` cachea por sesión, así que esto no agrega una
    llamada. Si todavía no llegaron, la sugerencia dice "en 2 categorías" en vez de los
    nombres — peor, pero cierto, y se actualiza sola cuando llegan.
  */
  const categorias = useNombresDeCategorias(alcance.categoryIds.length > 0);
  const sugerido = useMemo(
    () => recortar(describirRegla(regla, categorias === null ? {} : { categorias })),
    [regla, categorias],
  );
  const nombreFinal = nombre.trim() === '' ? sugerido : nombre.trim();

  // Recibe la regla por parámetro y no la lee del estado a propósito: "incluir las ofertas"
  // cambia `campos` y recalcula en el mismo gesto, y `setCampos` no se ve hasta el próximo
  // render. Pedir el preview con la regla vieja mostraría un plan que no es el que se va a
  // ejecutar, que es exactamente lo que la app no puede hacer.
  async function calcularPreview(reglaAUsar: Regla = regla): Promise<void> {
    setCalculando(true);
    setError(null);
    try {
      setPreview(await api.preview(nombreFinal, reglaAUsar, true));
      setPaso(3);
    } catch (excepcion) {
      setError(aErrorVisible(excepcion, 'No pudimos calcular la vista previa. Probá de nuevo en un rato.'));
    } finally {
      setCalculando(false);
    }
  }

  /**
   * La acción de la alerta `promotional_not_lower`.
   *
   * La oferta es lo que el comprador paga, y Tienda Nube acepta sin protestar un precio por
   * debajo de una oferta ya guardada (spike 3b): el aviso es la única defensa. Así que en
   * vez de avisar y quedarse, ofrece la salida que el motor ya sabe hacer — el mismo ajuste
   * sobre la oferta, conservando el porcentaje de descuento — y recalcula el plan.
   */
  async function incluirLasOfertas(): Promise<void> {
    setCampos('both');
    await calcularPreview({ ...regla, targetFields: 'both' });
  }

  async function ejecutar(): Promise<void> {
    if (preview === null) return;
    setEjecutando(true);
    setError(null);
    try {
      await api.ejecutar(preview.corridaId);
      navigate(`/corridas/${preview.corridaId}`);
    } catch (excepcion) {
      setError(aErrorVisible(excepcion, 'No pudimos arrancar la actualización. Probá de nuevo.'));
      setEjecutando(false);
    }
  }

  return (
    <Layout titulo="Nueva actualización">
      <PasosCabecera paso={paso} />

      {/*
        `freno` va sólo acá y no en las otras siete cajas de error de la app: el 429 del
        límite por tienda lo devuelven `POST /preview` y `POST /:id/ejecutar`, y las dos se
        llaman desde esta pantalla. Ponerlo en las demás sería un camino que nunca corre.
      */}
      {error !== null && (
        <ErrorCaja mensaje={error.mensaje} codigo={error.codigo} freno={esFreno(error)} />
      )}

      {copiadaDe !== null && (
        <Alert appearance="neutral" title="Copiamos la regla de una actualización anterior">
          <Text>
            {/*
              En minúscula y sin "el" adelante: `fechaHoraRelativa` devuelve "Hoy 18:42" o
              "Viernes 21:39" tanto como "08/08/2026, 21:42", y "corriste el Hoy 18:42" está
              mal escrito. En español los días de la semana van en minúscula, así que bajar
              todo es correcto en los cuatro casos.
            */}
            Es la misma que corriste {copiadaDe.toLowerCase()}. Podés cambiar lo que quieras
            antes de confirmar, y la vista previa se calcula de nuevo sobre los precios de hoy.
          </Text>
        </Alert>
      )}

      {noSePudoCopiar && (
        <Alert appearance="neutral" title="No pudimos copiar esa regla">
          <Text>Armala de nuevo acá: el formulario está vacío y funciona igual.</Text>
        </Alert>
      )}

      {copiando && <Cargando texto="Copiando la regla…" />}

      {!copiando && paso === 1 && (
        <PasoQue
          alcance={alcance}
          conteo={conteo}
          onAlcance={setAlcance}
          onSiguiente={() => setPaso(2)}
        />
      )}

      {!copiando && paso === 2 && (
        <PasoCuanto
          conteo={conteo}
          ajuste={ajuste}
          redondeo={redondeo}
          campos={campos}
          nombre={nombre}
          sugerido={sugerido}
          onNombre={setNombre}
          onAjuste={setAjuste}
          onRedondeo={setRedondeo}
          onCampos={setCampos}
          onAtras={() => setPaso(1)}
          onSiguiente={() => void calcularPreview()}
          calculando={calculando || bloqueada}
        />
      )}

      {!copiando && paso === 3 && preview !== null && (
        <PasoVistaPrevia
          preview={preview}
          ejecutando={ejecutando || bloqueada}
          onAtras={() => setPaso(2)}
          onEjecutar={() => void ejecutar()}
          // El botón sólo tiene sentido si la regla todavía no toca las ofertas: con
          // `both` ya elegido, el aviso significa otra cosa (la oferta se quedó arriba
          // igual) y aplicar lo mismo de nuevo no arregla nada.
          onIncluirOfertas={
            campos === 'price' && preview.avisos.some((a) => a.codigo === 'promotional_not_lower')
              ? () => void incluirLasOfertas()
              : null
          }
          recalculando={calculando}
        />
      )}

      {paso === 3 && calculando && <Cargando texto="Leyendo tu catálogo y calculando…" />}
    </Layout>
  );
}

// ---------------------------------------------------------------------------

function PasosCabecera({ paso }: { readonly paso: Paso }): ReactElement {
  const pasos = [
    { numero: 1, texto: 'Qué productos' },
    { numero: 2, texto: 'Cuánto' },
    { numero: 3, texto: 'Vista previa' },
  ];
  return (
    <Box display="flex" gap={{ xs: '2', md: '4' }} flexWrap="wrap">
      {pasos.map((item) => (
        <Text
          key={item.numero}
          fontWeight={item.numero === paso ? 'bold' : 'regular'}
          color={item.numero === paso ? 'primary-textLow' : 'neutral-textLow'}
        >
          {item.numero}. {item.texto}
        </Text>
      ))}
    </Box>
  );
}

// --- Paso 1: qué ------------------------------------------------------------

interface PasoQueProps {
  readonly alcance: Alcance;
  readonly conteo: EstadoDelConteo;
  readonly onAlcance: (valor: Alcance) => void;
  readonly onSiguiente: () => void;
}

function PasoQue({ alcance, conteo, onAlcance, onSiguiente }: PasoQueProps): ReactElement {
  const falta = sinCriterio(alcance);

  return (
    <Box display="flex" flexDirection="column" gap="4">
      {/*
        *** Acá había un "Ponele un nombre" y era el primer campo de la app. ***

        Le pedía al comerciante que bautizara algo que todavía no había armado: no sabe qué
        va a hacer, así que escribe "prueba" o "asdf". El nombre se pide al final del paso
        2, ya opcional, con la regla sugerida de placeholder.
      */}
      <CamposDeAlcance alcance={alcance} onAlcance={onAlcance} />

      <CuantoAgarra conteo={conteo} />

      {falta && (
        <Alert appearance="warning" title="Falta elegir qué productos">
          <Text>Elegí al menos una categoría, una marca o un rango de precios.</Text>
        </Alert>
      )}

      <Box className="acciones-a-lo-ancho" display="flex" justifyContent="flex-end">
        <Button size="large" appearance="primary" disabled={falta} onClick={onSiguiente}>
          Siguiente
        </Button>
      </Box>
    </Box>
  );
}

// --- Paso 2: cuánto ---------------------------------------------------------

/**
 * Cuántos productos y cuántas variantes agarra el alcance de este momento.
 *
 * Cuatro estados y ninguno molesta: sin criterio y desconocido no muestran nada, contando
 * dice que está contando, y el cero es el único que grita — porque es el único accionable,
 * y es exactamente el que antes aparecía dos pasos más tarde.
 */
function CuantoAgarra({ conteo }: { readonly conteo: EstadoDelConteo }): ReactElement | null {
  if (conteo.clase === 'sin_criterio' || conteo.clase === 'desconocido') return null;

  if (conteo.clase === 'contando') {
    return (
      <Franja>
        <Text fontSize="caption" color="neutral-textLow">
          Contando cuántos productos agarra…
        </Text>
      </Franja>
    );
  }

  if (conteo.productos === 0) {
    return (
      <Alert appearance="warning" title="Ningún producto coincide con este alcance">
        <Text>
          Probá ampliándolo: sacá un filtro de precio, sumá otra categoría, o elegí todo el
          catálogo. Si seguís así, la vista previa va a venir vacía.
        </Text>
      </Alert>
    );
  }

  return (
    <Franja>
      <Text fontSize="caption" color="neutral-textLow">
        Alcance
      </Text>
      <Text fontWeight="medium">
        {numero(conteo.productos)} productos · {numero(conteo.variantes)} variantes
      </Text>
    </Franja>
  );
}

/** El fondo tenue que hace que el conteo no quede flotando entre la tarjeta y el botón. */
function Franja({ children }: { readonly children: ReactNode }): ReactElement {
  return (
    <Box
      display="flex"
      gap="2"
      alignItems="baseline"
      flexWrap="wrap"
      padding="3"
      backgroundColor="neutral-surface"
      borderRadius="2"
    >
      {children}
    </Box>
  );
}

interface PasoCuantoProps {
  readonly conteo: EstadoDelConteo;
  readonly ajuste: Ajuste;
  readonly redondeo: Redondeo;
  readonly campos: CampoAfectado;
  /** Lo que escribió el comerciante. Vacío significa "usá el sugerido". */
  readonly nombre: string;
  /** La regla en una línea, que es el nombre por defecto. */
  readonly sugerido: string;
  readonly onNombre: (valor: string) => void;
  readonly onAjuste: (valor: Ajuste) => void;
  readonly onRedondeo: (valor: Redondeo) => void;
  readonly onCampos: (valor: CampoAfectado) => void;
  readonly onAtras: () => void;
  readonly onSiguiente: () => void;
  readonly calculando: boolean;
}

function PasoCuanto(props: PasoCuantoProps): ReactElement {
  return (
    <Box display="flex" flexDirection="column" gap="4">
      {/*
        El alcance sigue a la vista mientras se define el ajuste. Un "+12%" se piensa
        distinto sobre 32 variantes que sobre 3.000, y hasta ahora ese número no estaba en
        la pantalla donde se elige el número.
      */}
      <CuantoAgarra conteo={props.conteo} />

      <CamposDeAjuste
        ajuste={props.ajuste}
        redondeo={props.redondeo}
        campos={props.campos}
        onAjuste={props.onAjuste}
        onRedondeo={props.onRedondeo}
        onCampos={props.onCampos}
      />

      {/*
        El nombre, acá y opcional.

        *** El placeholder no es un ejemplo: es lo que se va a guardar. *** Sale de
        `describirRegla`, la misma función que escribe la línea del historial, así que
        dejarlo vacío produce exactamente el texto que después identifica la corrida. Y
        como el paso 2 es donde la regla ya está completa, la sugerencia es la definitiva.
      */}
      <Card>
        <Card.Header title="Nombre (opcional)" />
        <Card.Body>
          <Box display="flex" flexDirection="column" gap="1">
            <Input
              id="nombre"
              name="nombre"
              maxLength={MAX_NOMBRE}
              value={props.nombre}
              onChange={(evento) => props.onNombre(evento.currentTarget.value)}
              placeholder={props.sugerido}
            />
            <Text fontSize="caption" color="neutral-textLow">
              Si lo dejás vacío la llamamos &quot;{props.sugerido}&quot;.
            </Text>
          </Box>
        </Card.Body>
      </Card>

      <Box
        className="acciones-a-lo-ancho"
        display="flex"
        flexDirection={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        gap={{ xs: '3', md: '2' }}
      >
        <Button onClick={props.onAtras}>Atrás</Button>
        <Button
          size="large"
          appearance="primary"
          disabled={props.calculando}
          onClick={props.onSiguiente}
        >
          {props.calculando ? 'Calculando…' : 'Ver vista previa'}
        </Button>
      </Box>
    </Box>
  );
}

// --- Paso 3: vista previa ---------------------------------------------------

interface PasoVistaPreviaProps {
  readonly preview: ResumenPreview;
  readonly ejecutando: boolean;
  readonly onAtras: () => void;
  readonly onEjecutar: () => void;
  /** `null` cuando el aviso de la oferta no aparece o la regla ya toca las ofertas. */
  readonly onIncluirOfertas: (() => void) | null;
  readonly recalculando: boolean;
}

function PasoVistaPrevia({
  preview,
  ejecutando,
  onAtras,
  onEjecutar,
  onIncluirOfertas,
  recalculando,
}: PasoVistaPreviaProps): ReactElement {
  const nadaQueHacer = preview.totales.aActualizar === 0;

  return (
    <Box display="flex" flexDirection="column" gap="4">
      <Card>
        <Card.Body>
          {/*
            Cuatro números en un renglón entran en un monitor y no en 375px, donde
            terminarían en cuatro renglones de un número cada uno. En grilla de dos
            columnas se leen de un vistazo y ocupan la mitad del alto.
          */}
          <Box
            display="grid"
            gridTemplateColumns={{ xs: '1fr 1fr', md: 'repeat(4, max-content)' }}
            gap={{ xs: '3', md: '6' }}
          >
            <Metrica valor={numero(preview.totales.aActualizar)} etiqueta="variantes a actualizar" />
            <Metrica valor={numero(preview.totales.productos)} etiqueta="productos" />
            <Metrica valor={numero(preview.totales.sinCambios)} etiqueta="sin cambios" />
            <Metrica valor={numero(preview.totales.salteadas)} etiqueta="salteadas" />
          </Box>
        </Card.Body>
      </Card>

      {preview.avisos.length > 0 && (
        <Alert appearance="warning" title="Mirá esto antes de confirmar">
          <Box display="flex" flexDirection="column" gap="2">
            <Box display="flex" flexDirection="column" gap="1">
              {preview.avisos.map((aviso) => (
                <Text key={aviso.codigo}>
                  {numero(aviso.cantidad)}: {aviso.mensaje}
                </Text>
              ))}
            </Box>
            {onIncluirOfertas !== null && (
              <Box className="acciones-a-lo-ancho" display="flex" paddingTop="1">
                <Button
                  size="large"
                  appearance="primary"
                  disabled={recalculando}
                  onClick={onIncluirOfertas}
                >
                  {recalculando ? 'Recalculando…' : 'Actualizar también la oferta'}
                </Button>
              </Box>
            )}
          </Box>
        </Alert>
      )}

      {/*
        Cuánto va a tardar, antes de confirmar. Va en `neutral` y no en `warning` cuando es
        rápido, y en `warning` cuando las ofertas lo multiplican: es la única forma de que
        un número grande se lea como algo esperado y no como un error.
      */}
      {!nadaQueHacer && (
        <Alert
          appearance={preview.duracion.porOfertas ? 'warning' : 'neutral'}
          title="Cuánto va a tardar"
        >
          <Box display="flex" flexDirection="column" gap="1">
            <Text>{preview.duracion.texto}</Text>
            <Text fontSize="caption" color="neutral-textLow">
              Corre en segundo plano y por lotes: podés cerrar esta pantalla y seguir usando
              tu panel. Vas a ver el avance en el historial.
            </Text>
          </Box>
        </Alert>
      )}

      {/*
        Dos situaciones muy distintas caían en el mismo cartel: que la regla no agarrara
        ningún producto, y que agarrara muchos pero ninguno cambie de precio. La primera se
        arregla en el paso 1 y la segunda en el paso 2, así que decir "revisá el alcance o
        el ajuste" mandaba a la mitad de la gente al lugar equivocado.
      */}
      {nadaQueHacer && preview.totales.productos === 0 && (
        <Alert appearance="neutral" title="Ningún producto coincide con esta regla">
          <Text>
            Volvé al paso 1 y probá ampliando el alcance: sacá un filtro de precio, sumá otra
            categoría, o elegí todo el catálogo.
          </Text>
        </Alert>
      )}

      {nadaQueHacer && preview.totales.productos > 0 && (
        <Alert appearance="neutral" title="Los precios quedarían iguales">
          <Box display="flex" flexDirection="column" gap="1">
            <Text>
              La regla alcanza {numero(preview.totales.productos)} productos, pero ninguna
              variante cambia de precio.
            </Text>
            <Text fontSize="caption" color="neutral-textLow">
              {preview.totales.salteadas > 0
                ? `${numero(preview.totales.salteadas)} quedaron afuera por algún motivo — está en la columna "Estado" de la tabla. Volvé al paso 2 si querés cambiar el ajuste.`
                : 'Volvé al paso 2 y revisá el ajuste: un 0%, o un redondeo que devuelve el mismo precio, dejan todo como está.'}
            </Text>
          </Box>
        </Alert>
      )}

      <TablaItems corridaId={preview.corridaId} contexto="preview" />

      {/*
        *** El aviso de integraciones va acá, no arriba. ***

        Estaba antes de la tabla: seis líneas de advertencia entre el comerciante y el
        primer número de la pantalla que vino a ver. Lo que pasa con seis líneas ahí es que
        se saltean, y con ellas se saltea el resto del aviso.

        Acá abajo está pegado al botón que dispara los webhooks, que es el momento en que
        la advertencia significa algo y el único en que se puede hacer algo con ella.

        Los dos párrafos van separados a propósito: "van a recibir 20 avisos" y "puede que
        no se enteren" son ideas opuestas, y juntas en un bloque se leen como una
        contradicción.
      */}
      {preview.avisoIntegraciones !== null && !nadaQueHacer && (
        <Alert appearance="warning" title="Cómo se van a enterar tus otras aplicaciones">
          <Box display="flex" flexDirection="column" gap="2">
            {preview.avisoIntegraciones.map((parrafo) => (
              <Text key={parrafo.slice(0, 40)}>{parrafo}</Text>
            ))}
          </Box>
        </Alert>
      )}

      {/*
        El botón que escribe precios en la tienda de verdad. En pantalla angosta va solo
        en su renglón, a lo ancho y en tamaño grande: es lo último que se toca antes de
        que cambien 3.000 precios y no puede estar a ocho píxeles del botón del CSV.
      */}
      <Box
        display="flex"
        flexDirection={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        gap="2"
        flexWrap="wrap"
      >
        <Box className="acciones-a-lo-ancho" display="flex">
          <Button onClick={onAtras}>Atrás</Button>
        </Box>
        <Box
          className="acciones-a-lo-ancho"
          display="flex"
          flexDirection={{ xs: 'column', md: 'row' }}
          gap={{ xs: '4', md: '2' }}
        >
          <Button onClick={() => void api.descargarCsv(preview.corridaId)}>
            Descargar vista previa en CSV
          </Button>
          <Button
            size="large"
            appearance="primary"
            disabled={ejecutando || nadaQueHacer}
            onClick={onEjecutar}
          >
            {ejecutando ? 'Arrancando…' : `Actualizar ${numero(preview.totales.aActualizar)} precios`}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

function Metrica({
  valor,
  etiqueta,
}: {
  readonly valor: string;
  readonly etiqueta: string;
}): ReactElement {
  return (
    <Box display="flex" flexDirection="column">
      <Title as="h3">{valor}</Title>
      <Text fontSize="caption" color="neutral-textLow">
        {etiqueta}
      </Text>
    </Box>
  );
}
